import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { Checkbox, Radio } from '@/components/fields';

export default function MedicalConditions(): JSX.Element {
  const { errors, watch, setValue, clearErrors } = useFormContext();

  const hasConditions = watch('has_conditions') === 'has_conditions_yes';

  useEffect(() => {
    if (!hasConditions) {
      setValue('medical_conditions', []);
      clearErrors('medical_conditions');
    }
  }, [hasConditions]);

  return (
    <>
      <div className="mt-4 md:flex md:items-center">
        <div className="md:w-2/5 md:pr-4">
          <span className="block text-sm leading-5 font-normal text-gray-900">
            Do you suffer from any medical conditions that may impact your
            ability to drive? <sup className="text-red-600 font-sans">*</sup>
          </span>
        </div>
        <div className="md:w-2/5 md:pl-4">
          <div className="flex items-center">
            <Radio
              name="has_conditions"
              id="has_conditions_yes"
              className="mr-4"
            >
              Yes
            </Radio>
            <Radio
              name="has_conditions"
              id="has_conditions_no"
              className="mr-4"
            >
              No, I confirm that I don&apos;t suffer from any medical condition
              that would impact my ability to drive.
            </Radio>
          </div>
          {errors['has_conditions'] ? (
            <p className="mb-4 font-medium text-sm text-red-600">
              {errors['has_conditions'].message}
            </p>
          ) : null}
        </div>
      </div>
      {hasConditions && (
        <div className="mt-4 md:flex">
          <div className="md:w-2/5 md:pr-4">
            <span className="mt-2 block text-sm leading-5 font-normal text-gray-900 mb-5">
              Medical Conditions <sup className="text-red-600 font-sans">*</sup>
              <br />
              <span className="font-medium text-xs text-red-600">
                If you have a medical condition we may need further information
                from you or your doctor to confirm that you meet the medical
                standards of fitness to drive. When you have completed your
                application it will go to the medical department for
                consideration
              </span>
            </span>
          </div>
          <div className="md:w-2/5 md:pl-4">
            <Checkbox
              id="condition_1"
              name="medical_conditions"
              errorMessage={false}
            >
              Diabetes controlled by insulin (No need to notify us if you have
              diabetes controlled by tablets or other injections unless you
              experience hypoglycaemia requiring the assistance of another
              person)
            </Checkbox>
            <Checkbox
              id="condition_2"
              name="medical_conditions"
              errorMessage={false}
            >
              Epilepsy
            </Checkbox>
            <Checkbox
              id="condition_3"
              name="medical_conditions"
              errorMessage={false}
            >
              Any condition affecting both eyes, or the remaining eye if you
              only have one eye. Not including colour blindness or short or long
              sight
            </Checkbox>
            <Checkbox
              id="condition_4"
              name="medical_conditions"
              errorMessage={false}
            >
              Stroke, with any symptoms lasting longer than one month or TIAs
            </Checkbox>
            <Checkbox
              id="condition_5"
              name="medical_conditions"
              errorMessage={false}
            >
              Fits or blackouts
            </Checkbox>
            <Checkbox
              id="condition_6"
              name="medical_conditions"
              errorMessage={false}
            >
              Any type of brain surgery, severe head injury involving in-patient
              treatment, or brain tumour
            </Checkbox>
            <Checkbox
              id="condition_7"
              name="medical_conditions"
              errorMessage={false}
            >
              An implanted cardiac pacemaker
            </Checkbox>
            <Checkbox
              id="condition_8"
              name="medical_conditions"
              errorMessage={false}
            >
              An implanted cardiac defibrillator (ICD)
            </Checkbox>
            <Checkbox
              id="condition_9"
              name="medical_conditions"
              errorMessage={false}
            >
              Repeated attacks of sudden disabling giddiness
            </Checkbox>
            <Checkbox
              id="condition_10"
              name="medical_conditions"
              errorMessage={false}
            >
              Any other chronic neurological condition including Multiple
              Sclerosis, Motor Neurone and Huntington&apos;s disease
            </Checkbox>
            <Checkbox
              id="condition_11"
              name="medical_conditions"
              errorMessage={false}
            >
              A serious problem with memory or periods of confusion
            </Checkbox>
            <Checkbox
              id="condition_12"
              name="medical_conditions"
              errorMessage={false}
            >
              Persistent alcohol misuse or dependence
            </Checkbox>
            <Checkbox
              id="condition_13"
              name="medical_conditions"
              errorMessage={false}
            >
              Persistent drug misuse or dependence
            </Checkbox>
            <Checkbox
              id="condition_14"
              name="medical_conditions"
              errorMessage={false}
            >
              Serious psychiatric illness or mental ill health
            </Checkbox>
            <Checkbox
              id="condition_15"
              name="medical_conditions"
              errorMessage={false}
            >
              Parkinson&apos;s disease
            </Checkbox>
            <Checkbox
              id="condition_16"
              name="medical_conditions"
              errorMessage={false}
            >
              Sleep apnoea syndrome
            </Checkbox>
            <Checkbox
              id="condition_17"
              name="medical_conditions"
              errorMessage={false}
            >
              Narcolepsy
            </Checkbox>
            <Checkbox
              id="condition_18"
              name="medical_conditions"
              errorMessage={false}
            >
              Any condition affecting your visual field
            </Checkbox>
            <Checkbox
              id="condition_19"
              name="medical_conditions"
              errorMessage={false}
            >
              Total loss of sight in one eye
            </Checkbox>
            <Checkbox
              id="condition_20"
              name="medical_conditions"
              errorMessage={false}
            >
              Any persisting limb problem which needs driving to be restricted
              to certain types of vehicles or those with adapted controls
            </Checkbox>
            <Checkbox id="condition_21" name="medical_conditions">
              Severe learning disability
            </Checkbox>
          </div>
        </div>
      )}
    </>
  );
}
